import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import ListHeader from "@/common/components/ListHeader";
import Table from "@/common/components/Table";
import TextField from "@/common/components/TextField";
import useDebounce from "@/common/hooks/useDebounce";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { FileStackIcon, SearchIcon } from "lucide-react";
import { governmentAllocationsPendingListTableColumns } from "../../constants/governmentAllocationEntriesNotCompletedTableColumns.constant";
import { useGovernmentAllocationEntriesNotCompleted } from "../../hooks/useGovernmentAllocationEntriesNotCompleted";
import { Mapper } from "@/common/services/mapper";
import { GovermnentAllocationEntriesNotCompletedTransformer } from "../../services/GovernmentAllocationEntriesNotCompletedTransformer";
import ListFilters from "@/common/components/ListFilters";
import { useCompanies } from "@/features/companies/hooks/useCompanies";
import { CompaniesTransformer } from "@/features/companies/services/companiesTransformer";
import { CompanyBranchData } from "@/common/types/companyBranches.type";
import { Option } from '@/common/types/option'
import { useAvailableCompanyBranches } from "@/features/privateCompanies/hooks/companyBranches/useAvailableCompanyBranches";

const GovernmentAllocationsEntriesNotCompletedList = () => {
  const { filterParams, setFilterParams } = useFilterParams();

  const [search, setSearch] = useDebounce(filterParams.search);
  const [searchCompany, setSearchCompany] = useDebounce('')
  const [searchCompanyBranch, setSearchCompanyBranch] = useDebounce('')

  const { data: companyOptions } = useCompanies({
    filters: {
      search: searchCompany,
    },
    mapper: Mapper.mapWithTransformer({ transformer: CompaniesTransformer.optionsTransformer() }),
  });

  const { data: companyBranchesOptions } = useAvailableCompanyBranches({
    companyId: filterParams.client?.value,
    filters: {
      search: searchCompanyBranch,
    },
    mapper: Mapper.mapToOptions<CompanyBranchData>({ valueFieldName: 'id', labelFieldName: 'display_name' })
  })

  const {
    data: pendingGovernmentAllocations,
    isPending: pendingGovernmentAllocationsIsPending,
    meta: pendingGovernmentAllocationsMeta,
    error: pendingGovernmentAllocationsError = {} as any,
    refetch: refetchPendingGovernmentAllocations,
  } = useGovernmentAllocationEntriesNotCompleted({
    filters: {
      search,
      page: filterParams.page || 1,
      companyId: filterParams.client?.value,
      branchId: filterParams.clientBranch?.value,
    },
    mapper: Mapper.mapWithTransformer({ transformer: GovermnentAllocationEntriesNotCompletedTransformer.tableTransformer() }),
  });

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        Icon={<FileStackIcon className="w-10 h-10" strokeWidth={1} />}
        title="Empenhos pendentes"
      />

      <ErrorMessageWrapper
        className="h-[calc(100vh-200px)]"
        statusCode={pendingGovernmentAllocationsError?.status}
        refetchFunction={refetchPendingGovernmentAllocations}
      >
        <div className="h-[calc(100vh-200px)] flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <TextField
              LeftIcon={<SearchIcon />}
              placeholder="Pesquisar por empresa"
              className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
              onChange={(e) => {
                setSearch((e.target as HTMLInputElement).value);
                setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }));
              }}
              value={filterParams.search}
            />

            <ListFilters
              filters={[
                {
                  className: 'w-56',
                  onChange: (client) => setFilterParams((prev) => ({ ...prev, client, clientBranch: null })),
                  value: filterParams.client,
                  onInputChange: (value) => setSearchCompany(value),
                  options: [{ label: 'Todas', value: '' }, ...((companyOptions || []) as Option[])],
                  placeholder: 'Empresa',
                },
                {
                  className: `w-56 ${!filterParams.client?.value ? 'opacity-70' : ''}`,
                  onChange: (clientBranch) => setFilterParams((prev) => ({ ...prev, clientBranch })),
                  value: filterParams.clientBranch || null,
                  onInputChange: setSearchCompanyBranch,
                  options: [{ label: 'Todas', value: '' }, ...((companyBranchesOptions || []) as Option[])],
                  placeholder: 'Filial da empresa',
                  disabled: !filterParams.client?.value,
                },
              ]}
            />
          </div>

          <Table
            columns={governmentAllocationsPendingListTableColumns}
            isLoadingData={pendingGovernmentAllocationsIsPending}
            page={filterParams.page || 1}
            rows={pendingGovernmentAllocations}
            totalPages={pendingGovernmentAllocationsMeta.total_pages}
            totalCount={pendingGovernmentAllocationsMeta.total}
            hasPagination
            setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
            rowEmptyValue="--"
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  );
};

export default GovernmentAllocationsEntriesNotCompletedList;
